<template>
  <SimpleModalVue class="ecr-picker" ref="modal" :onClose="onClose" :onOpen="onOpen">
    <template v-slot:header>
      <span>Add One or More Saleable Part for Comparison</span>
    </template>
    <template v-slot:body>
      <div class="picker-container">
        <div class="selector-container">
          <div class="search-container">
            <input id="search-field" class="e-input" type="search" @change="searchCostStudies" placeholder="Search" />
            <div class="ico-btn" v-if="selectorListHeader != 'Cost Studies'" :title="`Back to ${previousListHeader}`"
              @click="onBackClick"><i class="fa-solid fa-chevron-left"></i></div>
            <div class="ico-btn" @click="clearSearch" title="Clear Search"><i class="fas fa-times"></i></div>
          </div>
          <div class="selector-list-container">
            <ejs-listview id="selector-list" ref="selectorList" showHeader="true" :headerTitle="selectorListHeader"
              :fields="{ tooltip: 'text' }" :dataSource="searchData" @select="onSelect" cssClass='e-list-template'
              :template="getSelectorListTemplate" :actionBegin="onSelectorActionBegin"
              :actionComplete="onSelectorActionComplete">
            </ejs-listview>
            <div ref="selectorSpinner" id="selector-spinner"></div>
          </div>
        </div>
        <div class="selected-item-list">
          <div>
            <h4>Selected Saleable Parts</h4>
          </div>
          <ejs-listview id="selected-list" ref="selectedList" :dataSource="selectedItemRefs" cssClass='e-list-template'
            :template="getResultListTemplate" :actionComplete="onSelectedActionComplete">
          </ejs-listview>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div id="ok-btn" class="e-control e-btn e-lib e-primary e-flat" @click="modal.closeModal()">Ok</div>
    </template>
  </SimpleModalVue>
</template>

<script>
import { ListViewComponent } from '@syncfusion/ej2-vue-lists';
export default {
  components: {
    "ejs-listview": ListViewComponent
  },
  data() {
    return {
      searchData: []
    }
  }

};
</script>


<script setup>
import { defineEmits, defineProps, defineExpose, ref, createApp, watch } from 'vue';
import { createSpinner, showSpinner, hideSpinner } from '@syncfusion/ej2-vue-popups';
import { DialogUtility } from '@syncfusion/ej2-vue-popups';
import { EcrPickerLevels as levels } from '../../modules/Constants';
import SimpleModalVue from './SimpleModal.vue';
import api from '../../modules/ApiClient';
import Session from '../../modules/Session';
import StudyListTemplate from '../templates/StudyListTemplate.vue';
import SaleableListTemplate from '../templates/SaleableListTemplate.vue';
import VersionListTemplate from '../templates/VersionListTemplate.vue';
import SnapshotListTemplate from '../templates/SnapshotListTemplate.vue';
import SelectedListTemplate from '../templates/SelectedListTemplate.vue';


const dialog = ref(null);
const searchData = ref([]);
const modal = ref({});
const selectorList = ref({});
const selectedList = ref({});
const selectorSpinner = ref({});
const selectorListHeader = ref(levels.COST_STUDIES);
const selectedItemRefs = ref([]);
const searchPathData = [];
let selectedItems = [];
let currentAggregateData = {};

/* Expected Model Structure
  [
    {
      id: string
      urlId: string
      text: string
      data: any
    },
    ...
  ]
*/
const props = defineProps({
  modelValue: { required: true },
  topLevel: {
    type: String,
    default: "Saleable Parts",
    validator(value) { return Object.values(levels).includes(value); }
  }
});

const emit = defineEmits(['update:modelValue', 'close']);

defineExpose({
  openModal: () => modal.value.openModal(),
  closeModal: () => modal.value.closeModal(),
})


watch(() => props.modelValue, (current) => {
  if (current.length === 0 || current.find(x => x.id !== undefined)) return;

  dialog.value = DialogUtility.alert({
    title: 'Loading Component Walk',
    content: 'Loading Business Case data from NCAS. Depending on the size of the selected Business Case this may take awhile. Please Wait',
    okButton: { cssClass: 'hidden' },
    showCloseIcon: false,
    closeOnEscape: false,
    animationSettings: { effect: 'Zoom' }
  });

  // Check modelValue for raw data if missing pull
  Session.WaitToBeAuthenticated(() => {
    Promise.all(Array.from(current).map(x => pullData(x))
    ).then((results) => {
      if (!results) return;

      selectedItems = results;
      selectedItemRefs.value = Array.from(selectedItems).map(x => generateItemRefs(x));
      emit('update:modelValue', selectedItems);
      dialog.value.hide();
    });
  });
}, { deep: true })

function onOpen() {

}

function onClose() {
  clearSearch();
  emit('update:modelValue', selectedItems);
  emit('close');
}

function searchCostStudies(event) {
  let searchValue = event.target.value;
  if (searchValue == "" || searchValue === undefined) return;
  const spinner = document.getElementById("selector-spinner");
  createSpinner({ target: spinner, type: "Bootstrap5" });
  showSpinner(spinner);
  selectorListHeader.value = levels.COST_STUDIES;
  searchData.value = [];
  currentAggregateData = {};
  // Display Loading 
  Session.WaitToBeAuthenticated(() => {
    api.GetCostStudies(searchValue).then((response) => {
      let data = response.map(x => generateCostStudyModel(x));
      searchData.value = data;
      hideSpinner(spinner);
    });
  });
}

function onSelect(args) {
  let spinner = document.getElementById("selector-spinner");
  showSpinner(spinner);
  const selected = searchData.value[args.index];

  switch (selectorListHeader.value) {
    case levels.COST_STUDIES: {
      selectorListHeader.value = levels.VERSIONS;
      searchPathData.push(searchData.value);
      searchData.value = [];
      currentAggregateData.costStudy = selected;
      Session.WaitToBeAuthenticated(() => {
        api.GetAvailableVersions(currentAggregateData.costStudy.id).then((response) => {
          let data = response.map(x => generateVersionModel(x.Version));
          searchData.value = data;
        });
      });
      break;
    }
    case levels.VERSIONS: {
      selectorListHeader.value = levels.SNAPSHOTS;
      searchPathData.push(searchData.value);
      searchData.value = [];
      currentAggregateData.version = selected;
      Session.WaitToBeAuthenticated(() => {
        Promise.all([
          api.GetCostStudy(currentAggregateData.costStudy.id, currentAggregateData.version.id),
          api.GetSnapshots(currentAggregateData.costStudy.id, currentAggregateData.version.id)
        ])
          .then((results) => {
            currentAggregateData.versionDetail = results[0];
            let data = results[1].map(x => generateSnapshotModel(x));
            searchData.value = data;
          });
      });
      break;
    }
    case levels.SNAPSHOTS: {
      selectorListHeader.value = levels.SALEABLE_PARTS;
      searchPathData.push(searchData.value);
      searchData.value = [];
      currentAggregateData.snapshot = selected;
      let data = currentAggregateData.snapshot.data.SaleableParts.map(x => generateSaleablePartModel(x));
      searchData.value = data;
      break;
    }
    case levels.SALEABLE_PARTS: {
      hideSpinner(spinner);

      let urlId = `${currentAggregateData.costStudy.id}|${currentAggregateData.version.id}|${currentAggregateData.snapshot.text}|${selected.number ?? ""}|${selected.suffix ?? ""}|${selected.rev ?? ""}`;
      if (selectedList.value.dataSource.find(x => x.id === btoa(urlId))) {
        break;
      }

      let selPart = {
        costStudy: currentAggregateData.costStudy,
        version: currentAggregateData.version,
        versionDetail: currentAggregateData.versionDetail,
        snapshot: currentAggregateData.snapshot,
        saleablePart: selected,
        urlId,
        id: btoa(urlId),
      };

      selPart.display = `${selPart.costStudy.ecrNumber} - ${selPart.version.text} - ${selPart.snapshot.text} - ${selPart.saleablePart.number}${selPart.saleablePart.suffix}${selPart.saleablePart.rev ? ` ${selPart.saleablePart.rev}` : ''}`

      selectedItems.push(selPart);
      let itemRef = generateItemRefs(selPart);
      selectedItemRefs.value.push(itemRef)
      selectedList.value.addItem([itemRef]);
      onSelectedActionComplete();
      break;
    }
  }
}

function onBackClick() {
  if (searchPathData.length == 0) return;
  if (searchPathData.length == 1) currentAggregateData = {};

  showSpinner(document.getElementById("selector-spinner"));

  let pathData = searchPathData.pop();

  //let selectorData = pathData.map(x => x.display);

  searchData.value = pathData;

  selectorListHeader.value = searchData.value[0].category;
}

function clearSearch() {
  document.getElementById("search-field").value = "";
  searchData.value = [];
  currentAggregateData = {};
  selectorListHeader.value = levels.COST_STUDIES;
  hideSpinner(document.getElementById("selector-spinner"));
}

function getSelectorListTemplate() {
  let selectorListTemplate = {};
  switch (selectorListHeader.value) {
    case levels.COST_STUDIES: selectorListTemplate = createApp().component("StudyListTemplate", StudyListTemplate); break;
    case levels.VERSIONS: selectorListTemplate = createApp().component("VersionListTemplate", VersionListTemplate); break;
    case levels.SNAPSHOTS: selectorListTemplate = createApp().component("SnapshotListTemplate", SnapshotListTemplate); break;
    case levels.SALEABLE_PARTS: selectorListTemplate = createApp().component("SaleableListTemplate", SaleableListTemplate); break;
  }
  return { template: selectorListTemplate }
}

function getResultListTemplate() {
  const saleableListTemplate = createApp().component("SelectedListTemplate", SelectedListTemplate)
  return { template: saleableListTemplate }
}

function removeSelected(event) {
  event.stopPropagation();
  let item = event.currentTarget.parentElement.parentElement.parentElement;

  onSelectedActionComplete();

  let rmvItem = selectedList.value.findItem(item);
  selectedList.value.removeItem(item);

  let index = selectedItems.findIndex(x => x.id == rmvItem.id);
  if (index > -1) selectedItems.splice(index, 1);

  let refIndex = selectedItemRefs.value.findIndex(x => x.id == rmvItem.id);
  if (refIndex > -1) selectedItemRefs.value.splice(refIndex, 1);
}

function onSelectorActionBegin() {
}

function onSelectorActionComplete() {
  hideSpinner(document.getElementById("selector-spinner"));
}

function onSelectedActionComplete() {
  let rmvBtns = document.getElementsByClassName("remove-btn");
  Array.from(rmvBtns).forEach((elem) => {
    elem.addEventListener("click", removeSelected);
  });
}

async function pullData(data) {
  const output = {}
  const partParams = Array.from(data.urlId.split("|"));
  if (partParams.length < 2) {
    // Display error not enough info
  }

  const costStudyId = partParams[0] ? partParams[0] : null
  const versionId = partParams[1] ? partParams[1] : null

  const csOverview = await api.GetOverview(costStudyId)
  output.costStudy = generateCostStudyModel(csOverview);

  const ver = await api.GetCostStudyVersion(costStudyId, versionId);
  output.version = generateVersionModel(ver);

  output.versionDetail = await api.GetCostStudy(costStudyId, versionId);

  if (partParams.length > 2) {
    const snapshotId = partParams[2]
    const snapshots = await api.GetSnapshots(costStudyId, versionId);
    output.snapshot = generateSnapshotModel(snapshots.find(x => x.GroupName == snapshotId));
  }

  if (partParams.length > 3) {
    const partNumber = partParams[3] ? partParams[3] : null
    const partSuffix = partParams[4] ? partParams[4] : null
    const partRev = partParams[5] ? partParams[5] : null
    const part = output.snapshot.data.SaleableParts
      .find(x => x.Part.Number.Fields[0] == partNumber
        && x.Part.Suffix?.Fields[0]?.Fields[0] == partSuffix
        && x.Part.DrawingRevision?.Fields[0]?.Fields[0] == partRev);
    output.saleablePart = generateSaleablePartModel(part);
  }

  output.urlId = data.urlId
  output.id = btoa(data.urlId);
  output.display = `${output.costStudy.ecrNumber} - ${output.version.text} - ${output.snapshot.text} - ${output.saleablePart.number}${output.saleablePart.suffix ?? ""}${output.saleablePart.rev ? ` ${output.saleablePart.rev}` : ''}`
  return output;
}

function generateCostStudyModel(costStudyRaw) {
  return {
    text: costStudyRaw.Name,
    ecrNumber: costStudyRaw.EcrNumber,
    status: costStudyRaw.Status,
    id: costStudyRaw.Id,
    category: levels.COST_STUDIES,
    rev: costStudyRaw.Revision,
    data: costStudyRaw
  };
}

function generateVersionModel(versionRaw) {
  return {
    text: versionRaw.Name,
    id: versionRaw.id,
    notes: versionRaw.Notes,
    category: levels.VERSIONS,
    data: versionRaw
  };
}

function generateSnapshotModel(snapshotRaw) {
  return {
    text: snapshotRaw.GroupName,
    id: snapshotRaw.id,
    category: levels.SNAPSHOTS,
    data: snapshotRaw
  }
}

function generateSaleablePartModel(partRaw) {
  const number = partRaw.Part.Number.Fields[0] ?? null;
  const suffix = partRaw.Part.Suffix?.Fields[0]?.Fields[0] ?? null;
  const rev = partRaw.Part.DrawingRevision?.Fields[0]?.Fields[0] ?? null;
  return {
    text: `${number}${suffix ?? ""}${rev ? `-${rev}` : ""}`,
    name: partRaw.Name.Value?.Fields[0],
    id: partRaw.Id,
    number: number,
    suffix,
    rev,
    category: levels.SALEABLE_PARTS,
    data: partRaw
  };
}

function generateItemRefs(itemModel) {
  let itemRef = {
    id: itemModel.id,
    partText: itemModel.saleablePart.text,
    partName: itemModel.saleablePart.name,
    partSuffix: itemModel.saleablePart.suffix,
    partRev: itemModel.saleablePart.rev,
    ecrNumber: itemModel.costStudy.ecrNumber,
    csText: itemModel.costStudy.text,
    version: itemModel.version.text,
    snapshot: itemModel.snapshot.text
  };
  return itemRef;
}

</script>

<style>
#ok-btn {
  float: right;
}

.selector-container {
  width: 50%;
  float: left;
}

.search-container {
  margin-bottom: 10px;
}

.search-container .e-input {
  display: inline;
  width: 75%;
}

.search-container .ico-btn {
  display: inline;
  margin-left: 5px;
  padding: 5px;
  height: 32px;
  font-size: 18px;
  cursor: pointer;
}


.selected-item-list {
  width: 48%;
  float: right;
}

.selected-item-list h4 {
  margin: 0 0 10px 0;
  padding: 7px 20px;
}

.selector-list-container {
  position: relative;
}

#selected-list,
#selector-list {
  display: block;
  border: 1px solid;
  border-color: #ccc;
  border-color: rgba(0, 0, 0, 0.12);
  height: 600px;
  width: 100%;
}

#selected-list .e-list-item,
#selector-list .e-list-item {
  padding: 0;
  cursor: pointer;
  box-sizing: border-box;
}

#selector-list.e-listview .e-list-header {
  height: 30px;
  font-size: 15px;
}

.e-spinner-pane .e-spinner-inner .e-spin-bootstrap5 {
  fill: #e51937;
  stroke: #e51937;
}
</style>